import { useCallback, useEffect, useState } from "react";
import { View, StyleSheet, Platform, ViewStyle } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import AxioButton from "../../../components/axio-ds/Button/AxioButton";
import BlankLayout from "../../../layouts/BlankLayout";
import AITutorChatContainerV2 from "../../../containers/chat/AITutorChatContainerV2/AITutorChatContainerV2";
import AiTutorCompleteModal from "../../../containers/customCoursework/AiTutorCompleteModal";
import UpgradeModalContainer from "../../../containers/general/UpgradeModalContainer/UpgradeModalContainer";
import ExplorationMenu, {
  ExplorationMediaItem,
} from "../../../components/learning-activity/ExplorationMenu/ExplorationMenu";
import { colors, radii, spacing, typographyStyles, shadowStyles, webTransitions } from "../../../styles/design-system";
import { Objective } from "../../companion/LearningGoalsContainer";

type ChatData = {
  id?: string;
  conversation?: Array<{
    id?: string;
    content?: string;
    sender?: string;
    created?: string;
    files?: Array<{ id: string; fileName: string }>;
    todos?: Array<{ id: string; name: string; date?: string | null }>;
    assignments?: Array<{ id: string; name?: string | null; due_date?: string | null }>;
    visualizationBlocks?: Array<{
      id: string;
      visualizationId?: string | null;
      visualizationType?: string | null;
      visualizationDescription?: string | null;
      content?: string | null;
    }>;
  }>;
};

export interface AITutorChatLayoutContainerV2Props {
  resourceId: string;
  title: string;
  loading: boolean;
  backButtonText: string;
  onBackPress: () => void;
  onNextActivityPress?: () => void;
  onModalBackPress?: () => void;
  hasNextActivity: boolean;
  showUpgradeModal?: boolean;
  chatData: ChatData | undefined;
  chatLoading: boolean;
  objectives: Objective[];
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onStreamCompleted?: () => void;
  isScreenFocused?: boolean;
  explorationItems: ExplorationMediaItem[];
  onCloseUpgradeModal?: () => void;
  hideBackButton?: boolean;
}

const AITutorChatLayoutContainerV2 = ({
  resourceId,
  title,
  loading: _loading,
  backButtonText,
  onBackPress,
  onNextActivityPress,
  onModalBackPress,
  hasNextActivity,
  showUpgradeModal = false,
  chatData,
  chatLoading,
  objectives,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamingStateChange,
  onStreamCompleted,
  isScreenFocused = true,
  explorationItems,
  onCloseUpgradeModal,
  hideBackButton = false,
}: AITutorChatLayoutContainerV2Props) => {
  const [aiTutorCompleteModalVisible, setAiTutorCompleteModalVisible] = useState(false);
  const [isAiResponseStreaming, setIsAiResponseStreaming] = useState(false);
  const [shouldOpenCompletionModalAfterStreaming, setShouldOpenCompletionModalAfterStreaming] = useState(false);

  const openCompletionModal = useCallback(() => {
    setAiTutorCompleteModalVisible(true);
    setShouldOpenCompletionModalAfterStreaming(false);
    onStreamCompleted?.();
  }, [onStreamCompleted]);

  const handleStreamingStateChange = (isStreaming: boolean) => {
    setIsAiResponseStreaming(isStreaming);
    onStreamingStateChange?.(isStreaming);
  };

  const handleAiStreamCompleted = () => {
    if (!shouldOpenCompletionModalAfterStreaming) {
      return;
    }
    openCompletionModal();
  };

  useEffect(() => {
    if (!shouldOpenCompletionModalAfterStreaming || isAiResponseStreaming) {
      return;
    }
    openCompletionModal();
  }, [isAiResponseStreaming, openCompletionModal, shouldOpenCompletionModalAfterStreaming]);

  const handleBackPress = () => {
    setAiTutorCompleteModalVisible(false);
    (onModalBackPress ?? onBackPress)();
  };

  const handleNextPress = () => {
    setAiTutorCompleteModalVisible(false);
    onNextActivityPress?.();
  };

  const handleClosePress = () => {
    setAiTutorCompleteModalVisible(false);
  };

  const handleAllObjectivesCompleted = () => {
    setShouldOpenCompletionModalAfterStreaming(true);
    onAllObjectivesCompleted?.();
  };

  return (
    <BlankLayout>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {!hideBackButton && (
              <AxioButton
                onPress={onBackPress}
                size="sm"
                label={backButtonText}
                variant="secondary"
                style={styles.backButton}
                textStyle={styles.backButtonText}
                accessibilityLabel="Exit button"
              />
            )}
            <CustomText
              text={title}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              accessibilityRole="header"
              aria-level="1"
              role="heading"
              style={styles.headerTitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            />
          </View>
        </View>

        <View style={styles.content}>
          <View style={styles.chatArea}>
            <CustomText
              useTranslationText={false}
              text="Conversation"
              role="heading"
              aria-level={2}
              accessibilityRole="header"
              style={styles.srOnly}
            />
            <AITutorChatContainerV2
              chatData={chatData}
              chatLoading={chatLoading}
              resourceId={resourceId}
              isScreenFocused={isScreenFocused}
              objectives={objectives}
              onObjectiveCompleted={onObjectiveCompleted}
              onAllObjectivesCompleted={handleAllObjectivesCompleted}
              onStreamingStateChange={handleStreamingStateChange}
              onStreamCompleted={handleAiStreamCompleted}
            />
          </View>

          {explorationItems.length > 0 && <ExplorationMenu items={explorationItems} />}
        </View>

        <UpgradeModalContainer visible={showUpgradeModal} onClose={onCloseUpgradeModal ?? (() => {})} />
        <AiTutorCompleteModal
          visible={aiTutorCompleteModalVisible}
          onBackPress={handleBackPress}
          onClosePress={handleClosePress}
          onNextPress={handleNextPress}
          hasNextActivity={hasNextActivity}
          backButtonText={backButtonText}
        />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[200],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  backButton: {
    minHeight: 36,
    padding: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    ...shadowStyles.sm,
    ...(Platform.OS === "web" ? webTransitions.fast : {}),
  },
  backButtonText: {
    ...typographyStyles.textSm,
    fontWeight: "500",
    color: colors.grey[700],
  },
  headerTitle: {
    color: colors.grey[900],
  },
  content: {
    flex: 1,
    position: "relative",
    backgroundColor: colors.grey[50],
  },
  chatArea: {
    flex: 1,
    position: "relative",
  },
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  } as ViewStyle,
});

export default AITutorChatLayoutContainerV2;
